import {
  Minimize, Repeat, LayoutGrid, Pencil, PenTool, Grid,
  FileText, FileSpreadsheet, Presentation, ImageIcon, ScanText,
  Layers, Scissors, RotateCw, Trash2, FileOutput,
  Eye, MessageSquare, Lock, Unlock, Droplets, Hash, Crop, EyeOff,
  Send, GitCompare,
} from 'lucide-react';

/**
 * Shared tool categories — single source of truth for Sidebar + Dashboard.
 *
 * English strings are the fallback source of truth. Each category and tool
 * has an `i18nKey` that maps to an entry in `tools.json`. The UI layer
 * (Sidebar + Dashboard) resolves it via `t(key) || fallback`, so missing
 * translations gracefully fall through to English.
 */
export const CATEGORIES = [
  {
    id: 'convert',
    label: 'Convert',
    i18nKey: 'tools:categories.convert',
    icon: Repeat,
    iconName: 'repeat',
    colors: { bg: '#EFF6FF', fg: '#3B82F6' },
    tools: [
      { title: 'PDF Converter', i18nKey: 'tools:convert.title', descKey: 'tools:convert.description', description: 'Convert files to and from PDF. Images, Word, Excel, PowerPoint.', path: '/convert', feature: 'convert-image', icon: Repeat, iconName: 'repeat' },
      { title: 'PDF to Word', i18nKey: 'tools:convert.pdf_to_word', descKey: 'tools:convert.pdf_to_word_desc', description: 'Convert PDFs to editable DOC and DOCX documents easily.', path: '/convert?tab=from-pdf&format=word', feature: 'convert-image', icon: FileText, iconName: 'file-text', formatGroup: 'word', direction: 'from-pdf' },
      { title: 'Word to PDF', i18nKey: 'tools:convert.word_to_pdf', descKey: 'tools:convert.word_to_pdf_desc', description: 'Turn Word documents into polished, shareable PDFs.', path: '/convert?tab=to-pdf&from=word', feature: 'convert-image', icon: FileText, iconName: 'file-text', formatGroup: 'word', direction: 'to-pdf' },
      { title: 'PDF to Excel', i18nKey: 'tools:convert.pdf_to_excel', descKey: 'tools:convert.pdf_to_excel_desc', description: 'Pull data from PDFs into Excel spreadsheets in seconds.', path: '/convert?tab=from-pdf&format=excel', feature: 'convert-image', icon: FileSpreadsheet, iconName: 'file-spreadsheet', formatGroup: 'excel', direction: 'from-pdf' },
      { title: 'Excel to PDF', i18nKey: 'tools:convert.excel_to_pdf', descKey: 'tools:convert.excel_to_pdf_desc', description: 'Convert Excel spreadsheets to clean, printable PDFs.', path: '/convert?tab=to-pdf&from=excel', feature: 'convert-image', icon: FileSpreadsheet, iconName: 'file-spreadsheet', formatGroup: 'excel', direction: 'to-pdf' },
      { title: 'PDF to PowerPoint', i18nKey: 'tools:convert.pdf_to_ppt', descKey: 'tools:convert.pdf_to_ppt_desc', description: 'Turn PDFs into editable PPT and PPTX presentations.', path: '/convert?tab=from-pdf&format=powerpoint', feature: 'convert-image', icon: Presentation, iconName: 'presentation', formatGroup: 'powerpoint', direction: 'from-pdf' },
      { title: 'PowerPoint to PDF', i18nKey: 'tools:convert.ppt_to_pdf', descKey: 'tools:convert.ppt_to_pdf_desc', description: 'Share presentations as PDFs that look right everywhere.', path: '/convert?tab=to-pdf&from=powerpoint', feature: 'convert-image', icon: Presentation, iconName: 'presentation', formatGroup: 'powerpoint', direction: 'to-pdf' },
      { title: 'PDF to Image', i18nKey: 'tools:convert.pdf_to_image', descKey: 'tools:convert.pdf_to_image_desc', description: 'Convert PDF pages to JPG, PNG, or other image formats.', path: '/convert?tab=from-pdf&format=image', feature: 'convert-image', icon: ImageIcon, iconName: 'image', formatGroup: 'image', direction: 'from-pdf' },
      { title: 'Image to PDF', i18nKey: 'tools:convert.image_to_pdf', descKey: 'tools:convert.image_to_pdf_desc', description: 'Combine JPG and PNG images into a single PDF.', path: '/convert?tab=to-pdf&from=image', feature: 'convert-image', icon: ImageIcon, iconName: 'image', formatGroup: 'image', direction: 'to-pdf' },
      { title: 'OCR — Text Recognition', i18nKey: 'tools:ocr.title', descKey: 'tools:ocr.description', description: 'Convert scanned PDFs to searchable, selectable text.', path: '/ocr', feature: 'ocr', icon: ScanText, iconName: 'scan-text' },
    ],
  },
  {
    id: 'organize',
    label: 'Organize',
    i18nKey: 'tools:categories.organize',
    icon: LayoutGrid,
    iconName: 'layout-grid',
    colors: { bg: '#FEF2F2', fg: '#EF4444' },
    tools: [
      { title: 'Compress PDF', i18nKey: 'tools:compress.title', descKey: 'tools:compress.description', description: 'Reduce file size while keeping quality. Three compression levels.', path: '/compress', feature: 'compress', icon: Minimize, iconName: 'minimize' },
      { title: 'Merge PDF', i18nKey: 'tools:merge.title', descKey: 'tools:merge.description', description: 'Combine multiple PDFs into one document in the order you want.', path: '/merge', feature: 'merge', icon: Layers, iconName: 'layers' },
      { title: 'Split PDF', i18nKey: 'tools:split.title', descKey: 'tools:split.description', description: 'Separate one PDF into multiple files by page ranges.', path: '/split?tab=split', feature: 'split', icon: Scissors, iconName: 'scissors' },
      { title: 'Rotate Pages', i18nKey: 'tools:rotate.title', descKey: 'tools:rotate.description', description: 'Rotate PDF pages 90°, 180°, or 270° individually or in bulk.', path: '/rotate', feature: 'split', icon: RotateCw, iconName: 'rotate-cw' },
      { title: 'Delete Pages', i18nKey: 'tools:delete_pages.title', descKey: 'tools:delete_pages.description', description: 'Remove unwanted pages from your PDF visually.', path: '/delete', feature: 'split', icon: Trash2, iconName: 'trash-2' },
      { title: 'Extract Pages', i18nKey: 'tools:extract.title', descKey: 'tools:extract.description', description: 'Pull out specific pages from a PDF into a new file.', path: '/split?tab=extract', feature: 'split', icon: FileOutput, iconName: 'file-output' },
    ],
  },
  {
    id: 'edit',
    label: 'Edit',
    i18nKey: 'tools:categories.edit',
    icon: Pencil,
    iconName: 'pencil',
    colors: { bg: '#F5F3FF', fg: '#7C3AED' },
    tools: [
      { title: 'View PDF', i18nKey: 'tools:viewer.title', descKey: 'tools:viewer.description', description: 'Open and read PDFs in your browser with thumbnails and zoom.', action: 'openFile', feature: 'viewer', icon: Eye, iconName: 'eye' },
      { title: 'Edit PDF', i18nKey: 'tools:edit.title', descKey: 'tools:edit.description', description: 'Fill forms, add text, images, and annotations to any PDF.', action: 'openFile', feature: 'edit-mode', icon: Pencil, iconName: 'pencil' },
      { title: 'Annotate PDF', i18nKey: 'tools:annotate.title', descKey: 'tools:annotate.description', description: 'Highlight, draw, underline, and add notes to your documents.', action: 'openFileAnnotate', feature: 'annotations-basic', icon: MessageSquare, iconName: 'message-square' },
      { title: 'Edit Text', i18nKey: 'tools:edit_text.title', descKey: 'tools:edit_text.description', description: 'Modify existing text directly in your PDF. Change fonts and sizes.', action: 'openFileEditText', feature: 'edit-mode', icon: FileText, iconName: 'file-text', pro: true },
      { title: 'Crop PDF', i18nKey: 'tools:crop.title', descKey: 'tools:crop.description', description: 'Trim page margins visually. Auto-detect or use presets.', path: '/crop', feature: 'crop', icon: Crop, iconName: 'crop' },
      { title: 'Redact PDF', i18nKey: 'tools:redact.title', descKey: 'tools:redact.description', description: 'Permanently remove sensitive information from documents.', path: '/redact', feature: 'redact', icon: EyeOff, iconName: 'eye-off' },
      { title: 'Add Watermark', i18nKey: 'tools:watermark.title', descKey: 'tools:watermark.description', description: 'Stamp text or image watermarks on every page of your PDF.', path: '/watermark', feature: 'watermark', icon: Droplets, iconName: 'droplets' },
      { title: 'Page Numbers', i18nKey: 'tools:page_numbers.title', descKey: 'tools:page_numbers.description', description: 'Add page numbers to your PDF. Choose position, format, and font.', path: '/page-numbers', feature: 'page-numbers', icon: Hash, iconName: 'hash' },
    ],
  },
  {
    id: 'sign',
    label: 'Sign',
    i18nKey: 'tools:categories.sign',
    icon: PenTool,
    iconName: 'pen-tool',
    colors: { bg: '#FFF7ED', fg: '#EA580C' },
    tools: [
      { title: 'Sign PDF', i18nKey: 'tools:sign.title', descKey: 'tools:sign.description', description: 'Add handwritten, typed, or uploaded signatures to any PDF.', action: 'openFileSign', feature: 'sign', icon: PenTool, iconName: 'pen-tool' },
    ],
  },
  {
    id: 'esign',
    label: 'eSign',
    i18nKey: 'tools:categories.esign',
    icon: Send,
    iconName: 'send',
    colors: { bg: '#FEF3C7', fg: '#D97706' },
    tools: [
      { title: 'Request Signatures', i18nKey: 'tools:request_signatures.title', descKey: 'tools:request_signatures.description', description: 'Send documents for others to sign electronically.', path: '/esign', feature: 'esign', icon: Send, iconName: 'send', pro: true },
    ],
  },
  {
    id: 'more',
    label: 'More',
    i18nKey: 'tools:categories.more',
    icon: Grid,
    iconName: 'grid',
    colors: { bg: '#F0FDF4', fg: '#16A34A' },
    tools: [
      { title: 'Protect PDF', i18nKey: 'tools:protect.title', descKey: 'tools:protect.description', description: 'Encrypt your PDF with a password to restrict access.', path: '/protect?tab=protect', feature: 'protect', icon: Lock, iconName: 'lock', displayTab: 'sign', displayColors: { bg: '#FFF7ED', fg: '#EA580C' } },
      { title: 'Unlock PDF', i18nKey: 'tools:protect.unlock_title', descKey: 'tools:protect.unlock_description', description: 'Remove password protection from your PDF documents.', path: '/protect?tab=unlock', feature: 'protect', icon: Unlock, iconName: 'unlock', displayTab: 'sign', displayColors: { bg: '#FFF7ED', fg: '#EA580C' } },
      { title: 'Flatten PDF', i18nKey: 'tools:flatten.title', descKey: 'tools:flatten.description', description: 'Remove form fields and layers — make content permanent.', path: '/flatten', feature: 'flatten', icon: Layers, iconName: 'layers' },
      { title: 'Batch Process', i18nKey: 'tools:batch.title', descKey: 'tools:batch.description', description: 'Merge, compress, convert, or protect multiple files at once.', path: '/batch', feature: 'batch', icon: Grid, iconName: 'grid' },
      { title: 'Invoice Builder', i18nKey: 'tools:invoice.title', descKey: 'tools:invoice.description', description: 'Create professional invoices with line items, tax, and branding.', path: '/invoice', feature: 'invoice', icon: FileText, iconName: 'file-text' },
      { title: 'Compare PDFs', i18nKey: 'tools:compare.title', descKey: 'tools:compare.description', description: 'Spot differences between two PDF versions side-by-side.', path: '/compare', feature: 'compare', icon: GitCompare, iconName: 'git-compare' },
    ],
  },
];

/**
 * Dashboard tab layout — mirrors the scrivar.com homepage tool section.
 * Tools are pulled from CATEGORIES, respecting each tool's `displayTab` override.
 */
export const DASHBOARD_TABS = [
  { id: 'all', label: 'All', i18nKey: 'tools:dashboard.tabs.all' },
  { id: 'organize', label: 'Organize', i18nKey: 'tools:dashboard.tabs.organize' },
  { id: 'convert', label: 'Convert', i18nKey: 'tools:dashboard.tabs.convert' },
  { id: 'edit', label: 'Edit', i18nKey: 'tools:dashboard.tabs.edit' },
  { id: 'sign', label: 'Sign & Secure', i18nKey: 'tools:dashboard.tabs.sign' },
  { id: 'more', label: 'More Tools', i18nKey: 'tools:dashboard.tabs.more' },
];

/**
 * Maps a route pathname to the active sidebar category.
 */
export function getActiveCategory(pathname) {
  if (pathname === '/compress') return 'organize';
  if (pathname === '/convert' || pathname === '/ocr') return 'convert';
  if (['/merge', '/split', '/delete', '/rotate'].includes(pathname)) return 'organize';
  if (['/viewer', '/crop', '/redact', '/watermark', '/page-numbers'].includes(pathname)) return 'edit';
  if (pathname === '/sign') return 'sign';
  if (pathname.startsWith('/esign')) return 'esign';
  if (['/protect', '/flatten', '/batch', '/invoice', '/compare'].includes(pathname)) return 'more';
  return null;
}

/**
 * Translate a CATEGORIES entry or tool using the i18n key, falling back
 * to the English string if the key is missing or returns itself unchanged.
 */
export function localizedLabel(t, entry, field = 'label') {
  const key = entry.i18nKey;
  const fallback = entry[field] || entry.title || '';
  if (!key || !t) return fallback;
  const translated = t(key);
  return translated && translated !== key ? translated : fallback;
}

export function localizedToolTitle(t, tool) {
  if (!tool) return '';
  if (!tool.i18nKey || !t) return tool.title || '';
  const translated = t(tool.i18nKey);
  return translated && translated !== tool.i18nKey ? translated : tool.title || '';
}

export function localizedToolDescription(t, tool) {
  if (!tool) return '';
  if (!tool.descKey || !t) return tool.description || '';
  const translated = t(tool.descKey);
  return translated && translated !== tool.descKey ? translated : tool.description || '';
}

/**
 * Look up the tool entry whose `path` matches the given pathname (with optional query).
 * Used by Breadcrumb to label the current tool.
 *
 * Matches `path + search` exactly first, then falls back to a path-only match
 * so `/split?tab=extract` resolves to Extract Pages while `/split` alone resolves
 * to Split PDF.
 */
export function getToolByPath(pathname, search = '') {
  const fullPath = pathname + (search || '');
  const allTools = CATEGORIES.flatMap((c) => c.tools);

  const exact = allTools.find((t) => t.path === fullPath);
  if (exact) return exact;

  const pathOnly = allTools.find((t) => {
    if (!t.path) return false;
    const p = t.path.split('?')[0];
    return p === pathname;
  });
  return pathOnly || null;
}
